import { useCallback, useEffect, useState } from "react";
import { listWorkerApplications } from "../../workerApplications/workerApplicationsApi.js";
import { listWorkerProfiles } from "../../workerProfiles/workerProfilesApi.js";
import { normalizeCandidate } from "./buddyGuaranteeShared.js";

/**
 * Prompt 140B-R1: the real, person-first roster this feature is built on -
 * calls the same already-shipped, already-Agent-scoped
 * GET /api/backoffice/worker-applications (Quick Registered applicants) or
 * GET /api/backoffice/worker-profiles (Worker Profiles) endpoint every
 * other Agent Portal screen already uses, real server-side page/pageSize/
 * search throughout - never a client-side slice of an unpaged fetch, and
 * never a hardcoded "pageSize:100 is the whole truth" assumption. A person
 * is listed here regardless of how many buddy contacts they currently
 * have (including zero) - buddy data is merged on separately, by
 * BuddyGuaranteesPage.jsx, via useBuddyActiveCounts.js.
 *
 * Never fetches when `enabled` is false - lets the page render its guard/
 * loading state (or skip fetching an inactive tab's real page) without an
 * extra network call.
 */
export function useAgentPersonRoster(sourceType, { enabled, page = 1, pageSize = 20, search } = {}) {
  const [state, setState] = useState({ status: "idle", items: [], totalCount: null, error: null });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setState({ status: "idle", items: [], totalCount: null, error: null });
      return undefined;
    }

    let cancelled = false;
    setState((previous) => ({ status: "loading", items: previous.items, totalCount: previous.totalCount, error: null }));

    const request =
      sourceType === "WorkerProfile"
        ? listWorkerProfiles({ page, pageSize, search: search || undefined })
        : listWorkerApplications({ page, pageSize, search: search || undefined });

    request
      .then((data) => {
        if (cancelled) return;
        const items = (data?.items ?? []).map((item) => normalizeCandidate(sourceType, item));
        setState({ status: "ready", items, totalCount: data?.totalCount ?? 0, error: null });
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ status: "error", items: [], totalCount: null, error });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, sourceType, page, pageSize, search, reloadToken]);

  const refetch = useCallback(() => setReloadToken((token) => token + 1), []);

  return { ...state, refetch };
}
